import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../../utils/api";
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Lock,
  Shield,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  Card,
  Button,
  PageHeader,
  AdminLayout,
  LoadingSpinner,
  useToast,
} from "../components/AdminSharedComponents";

const EditUser = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "client",
  });
  const [originalData, setOriginalData] = useState({});
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: "admin", label: "Admin", description: "Full system access" },
    {
      value: "admin-produk",
      label: "Admin Produk",
      description: "Product management access",
    },
    {
      value: "admin-proyek",
      label: "Admin Proyek",
      description: "Project management access",
    },
    {
      value: "supervisor",
      label: "Supervisor",
      description: "Supervisory access",
    },
    { value: "client", label: "Client", description: "Client access only" },
  ];

  // Load user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setInitialLoading(true);
        const response = await api.get(`/api/users/${id}`);

        if (response.data.status === "success") {
          const userData = response.data.data;
          const initialData = {
            name: userData.name || "",
            email: userData.email || "",
            password: "",
            confirmPassword: "",
            role: userData.role || "client",
          };
          setFormData(initialData);
          setOriginalData(initialData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Gagal memuat data user");
        navigate("/admin/users");
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id, navigate]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nama lengkap wajib diisi";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Nama minimal 2 karakter";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email wajib diisi";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Format email tidak valid";
    }

    // Password validation only if password is provided
    if (formData.password) {
      if (formData.password.length < 6) {
        newErrors.password = "Password minimal 6 karakter";
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Konfirmasi password wajib diisi";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Konfirmasi password tidak sama";
      }
    } else if (formData.confirmPassword) {
      newErrors.password =
        "Password wajib diisi jika konfirmasi password diisi";
    }

    if (!formData.role) {
      newErrors.role = "Role wajib dipilih";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form has changes
  const hasChanges = () => {
    return (
      formData.name !== originalData.name ||
      formData.email !== originalData.email ||
      formData.role !== originalData.role ||
      formData.password !== ""
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Mohon periksa kembali data yang diisi");
      return;
    }

    if (!hasChanges()) {
      toast.info("Tidak ada perubahan untuk disimpan");
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...dataToSend } = formData;

      // Only send password if it's provided
      if (!dataToSend.password) {
        delete dataToSend.password;
      }

      const response = await api.put(`/api/users/${id}`, dataToSend);

      if (response.data.status === "success") {
        toast.success("User berhasil diperbarui");
        navigate("/admin/users");
      }
    } catch (error) {
      console.error("Error updating user:", error);

      if (error.response?.data?.error) {
        const errorMessage = error.response.data.error;
        if (errorMessage.includes("email")) {
          setErrors({ email: "Email sudah digunakan" });
          toast.error("Email sudah digunakan");
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error("Gagal memperbarui user. Silakan coba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/users");
  };

  if (initialLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-96">
          <LoadingSpinner size="large" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Edit User"
        subtitle="Update user information and permissions"
        actions={
          <Button variant="outline" onClick={handleCancel}>
            <div className="flex items-center">
              <ArrowLeft size={18} className="mr-2" />
              Back to Users
            </div>
          </Button>
        }
      />

      <div className="max-w-2xl mx-auto">
        <Card title="User Information" accentColor="primary">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <User size={16} className="mr-2" />
                Nama Lengkap
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Masukkan nama lengkap"
                disabled={loading}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Mail size={16} className="mr-2" />
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Masukkan alamat email"
                disabled={loading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Lock size={16} className="mr-2" />
                Password Baru
                <span className="text-gray-500 ml-2 text-xs">
                  (Kosongkan jika tidak ingin mengubah)
                </span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Masukkan password baru (minimal 6 karakter)"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff size={16} className="text-gray-400" />
                  ) : (
                    <Eye size={16} className="text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Lock size={16} className="mr-2" />
                Konfirmasi Password Baru
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                    errors.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Masukkan ulang password baru"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={16} className="text-gray-400" />
                  ) : (
                    <Eye size={16} className="text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Role Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Shield size={16} className="mr-2" />
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.role ? "border-red-500" : "border-gray-300"
                }`}
                disabled={loading}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role}</p>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !hasChanges()}
                className="flex items-center"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Update User
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default EditUser;
